import type { AnimationEvent } from '@angular/animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Output,
} from '@angular/core';
import { fadeOut } from '@freelancer/animations';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { IconColor } from '@freelancer/ui/icon';
import {
  PictureBackgroundImageStyles,
  PictureBgImageObjectFit,
} from '@freelancer/ui/picture';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { BannerUpsellColor, BannerUpsellSize } from './banner-upsell.types';

@Component({
  selector: 'fl-banner-upsell-button',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellButtonComponent {}

@Component({
  selector: 'fl-banner-upsell-title',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellTitleComponent {}

@Component({
  selector: 'fl-banner-upsell-small-title',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellSmallTitleComponent {}

@Component({
  selector: 'fl-banner-upsell-description',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellDescriptionComponent {}

@Component({
  selector: 'fl-banner-upsell-disclaimer',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellDisclaimerComponent {}

@Component({
  selector: 'fl-banner-upsell-picture',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellPictureComponent {}

@Component({
  selector: 'fl-banner-upsell-background-image',
  template: `
    <fl-picture
      [src]="src"
      [srcTablet]="srcTablet"
      [srcDesktop]="srcDesktop"
      [srcLargeDesktop]="srcLargeDesktop"
      [alt]="alt"
      [isBackgroundImage]="true"
      [backgroundStyles]="backgroundStyles"
      (imageLoaded)="handleImageLoaded()"
    >
    </fl-picture>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerUpsellBackgroundImageComponent {
  @Input() alt: string;
  @Input() src: string;
  @Input() srcTablet?: string;
  @Input() srcDesktop?: string;
  @Input() srcLargeDesktop?: string;
  @Input() backgroundStyles?: PictureBackgroundImageStyles = {
    position: 'right center',
    objectFit: PictureBgImageObjectFit.COVER,
  };

  @Output() imageLoaded = new EventEmitter<void>();

  handleImageLoaded(): void {
    this.imageLoaded.emit();
  }
}

@Component({
  selector: 'fl-banner-upsell',
  template: `
    <div
      class="BannerUpsell"
      [attr.data-full-height]="fullHeight"
      [attr.data-size]="size"
      [@hideElement]="state"
      (@hideElement.done)="animationDone($event)"
    >
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
        <fl-col
          class="BannerUpsell-content"
          [col]="12"
          [colTablet]="size === BannerUpsellSize.SMALL ? 10 : 8"
          [colDesktopSmall]="
            size === BannerUpsellSize.XLARGE
              ? 6
              : size === BannerUpsellSize.SMALL
                ? 10
                : 8
          "
          [flMarginBottom]="
            bannerUpsellPicture && size !== BannerUpsellSize.SMALL
              ? 'xsmall'
              : 'none'
          "
          [flMarginBottomTablet]="'none'"
        >
          <ng-content select="fl-banner-upsell-background-image"></ng-content>
          <fl-heading
            *ngIf="size !== BannerUpsellSize.SMALL"
            [color]="
              color === BannerUpsellColor.DARK
                ? HeadingColor.DARK
                : HeadingColor.LIGHT
            "
            [headingType]="HeadingType.H2"
            [size]="TextSize.MID"
            [flMarginBottom]="'xxsmall'"
          >
            <ng-content select="fl-banner-upsell-title"></ng-content>
          </fl-heading>
          <fl-text
            *ngIf="size === BannerUpsellSize.SMALL"
            class="TextDark"
            [color]="
              color === BannerUpsellColor.DARK
                ? FontColor.INHERIT
                : FontColor.LIGHT
            "
            [size]="TextSize.XXSMALL"
            [flMarginBottom]="'none'"
            [weight]="FontWeight.BOLD"
          >
            <ng-content select="fl-banner-upsell-small-title"></ng-content>
          </fl-text>
          <fl-text
            class="TextDark"
            [color]="
              color === BannerUpsellColor.DARK
                ? FontColor.INHERIT
                : FontColor.LIGHT
            "
            [size]="
              size === BannerUpsellSize.SMALL
                ? TextSize.XXSMALL
                : TextSize.SMALL
            "
            [flMarginBottom]="
              bannerUpsellButton || bannerUpsellDisclaimer ? 'xsmall' : 'none'
            "
          >
            <ng-content select="fl-banner-upsell-description"></ng-content>
          </fl-text>
          <fl-text
            class="TextDark"
            [color]="
              color === BannerUpsellColor.DARK
                ? FontColor.INHERIT
                : FontColor.LIGHT
            "
            [size]="
              size === BannerUpsellSize.SMALL
                ? TextSize.XXXSMALL
                : TextSize.XXSMALL
            "
          >
            <ng-content select="fl-banner-upsell-disclaimer"></ng-content>
          </fl-text>
          <ng-content select="fl-banner-upsell-button"></ng-content>
        </fl-col>
        <fl-col
          class="BannerUpsell-picture"
          *ngIf="bannerUpsellPicture"
          [col]="12"
          [colTablet]="size === BannerUpsellSize.SMALL ? 2 : 4"
          [colDesktopSmall]="
            size === BannerUpsellSize.XLARGE
              ? 6
              : size === BannerUpsellSize.SMALL
                ? 2
                : 4
          "
          [flHideMobile]="size === BannerUpsellSize.SMALL"
        >
          <ng-content select="fl-banner-upsell-picture"></ng-content>
        </fl-col>
      </fl-grid>
      <fl-button
        *ngIf="closeable"
        class="BannerUpsell-closeButton"
        (click)="handleCloseEvent($event)"
      >
        <fl-icon
          i18n-label="Banner upsell close icon label"
          label="Close banner upsell"
          [name]="'ui-close'"
          [color]="_closeButtonColor"
        >
        </fl-icon>
      </fl-button>
    </div>
  `,
  styleUrls: ['./banner-upsell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hideElement', [
      transition('visible => hidden', [
        useAnimation(fadeOut, {
          params: { duration: '500' },
        }),
      ]),
    ]),
  ],
})
export class BannerUpsellComponent implements OnInit {
  BannerUpsellColor = BannerUpsellColor;
  BannerUpsellSize = BannerUpsellSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  IconColor = IconColor;
  FontColor = FontColor;
  TextSize = TextSize;
  FontWeight = FontWeight;
  VerticalAlignment = VerticalAlignment;

  state = 'visible';

  @HostBinding('class.IsHidden') bannerHidden = false;

  @Input()
  color: BannerUpsellColor = BannerUpsellColor.LIGHT;
  @Input() closeButtonColor: IconColor;
  @Input() size: BannerUpsellSize = BannerUpsellSize.LARGE;
  @Input() closeable = true;
  @Input() fullHeight = false;

  @Output() closeEvent = new EventEmitter<void>();

  @ContentChild(BannerUpsellPictureComponent)
  bannerUpsellPicture: BannerUpsellPictureComponent;
  @ContentChild(BannerUpsellButtonComponent)
  bannerUpsellButton: BannerUpsellButtonComponent;
  @ContentChild(BannerUpsellDisclaimerComponent)
  bannerUpsellDisclaimer: BannerUpsellDisclaimerComponent;

  _closeButtonColor: IconColor;

  constructor(@Inject(UI_CONFIG) public uiConfig: UiConfig) {}

  ngOnInit(): void {
    if (this.closeButtonColor) {
      // If a close button color is given, overwrite the default color.
      this._closeButtonColor = this.closeButtonColor;
    } else {
      this._closeButtonColor =
        this.color === BannerUpsellColor.DARK
          ? IconColor.FOREGROUND
          : IconColor.LIGHT;
    }
  }

  handleCloseEvent(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.state = 'hidden';
  }

  animationDone(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.bannerHidden = true;
      this.closeEvent.emit();
    }
  }
}
